<template lang="pug">
  .main-container
    .public-registry
      err404(v-if="error === 'not_found'")
      loading(v-else-if="!registry")
      .enter-password(v-else-if="registry.passwordRequired")
        h4.center {{__('Enter registry password to continue')}}
        RogueInput(v-model="pass" label="Password" :required="true")
        RogueButton(@click="load()" :loading="isLoading") {{__('Continue')}}
        Message.err(v-if="error === 'invalid_password'" status="error")
          Icon.left-icon(name="alert-error")
          | {{__('Invalid Password')}}
      .registry-details(v-else)
        h1 {{details.name}}
        em
          h5.from {{__('Shared by {name}', {name})}}
        registry-meta-details.details(:list="registry")
        .sort
          RogueSelect(:options="filterOptions" v-model="filterBy")
          RogueSelect(:options="sortOptions" v-model="sortBy")
        .no-products.center(v-if="items.length === 0")
          h3 {{__('No products.')}}
        RegistryItem.item(v-for="item in visibleItems" :key="item._id" :list="registry" :buyRequest="item" :mine="false")
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead, type ReactiveHead } from '@unhead/vue'
import { useProductStore } from '@/js/stores/productStore'
import RegistryMetaDetails from '@/js/components/registry/registry-meta-details.vue'
import RegistryItem from '@/js/components/registry/registry-item.vue'
import { computePriceSync } from '@/js/helpers/price-utils'
import stockStatus from '@/js/helpers/stockStatus'
import metadata from '@/js/helpers/metadata'
import { productHasChanged } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/RogueTypes'
import type { List } from '@/js/stores/listTypes'

// The list as the public endpoint returns it: the registry plus the flag it sets
// instead of the list body when a password is needed
type PublicRegistryList = List & { passwordRequired?: boolean }

type RegistryDetails = {
  name?: string,
  recipients?: { firstName?: string, lastName?: string }[],
}

// Save passwords in local memory so we dont have to re-enter them
// @todo persist? might not be desired UX
const SAVED_PASS: Record<string, string> = {}

const rogue = useRogue()
const route = useRoute()
const productStore = useProductStore()

const registry = ref<PublicRegistryList | null>(null)
const error = ref<string | null>(null)
const isLoading = ref(false)
const sortBy = ref('priority')
const filterBy = ref('all')
const pass = ref('')

// Dropdown configurations
const filterOptions = [
  { label: 'All Items', value: 'all' },
  { label: 'All Remaining Items', value: 'remaining' },
  { label: 'Remaining In-Stock Items', value: 'inStock' },
  { label: 'Purchased Items', value: 'purchased' },
]

const sortOptions = [
  { label: 'Priority', value: 'priority' },
  { label: 'Price (High to Low)', value: 'priceLow' },
  { label: 'Price (Low to High)', value: 'priceHigh' },
  { label: 'Number Remaining', value: 'remaining' },
]

const products = computed(() => productStore.products)

// Shortcut to access registry metadata
const details = computed<RegistryDetails>(() => (registry.value?.metadata?.registry || {}) as RegistryDetails)

const items = computed<BuyRequest[]>(() => {
  if (!registry.value?.items) {
    return []
  }

  const priorities = ['highest', 'high', 'medium', 'low', 'lowest']

  // Filter and sort the items of the registry using the dropdown options
  return registry.value.items.slice(0)
    .filter(item => {
      const meta = item.metadata?.registry || {}

      if (filterBy.value === 'remaining') {
        return (meta.wanted as number) > (meta.purchased || 0)
      }
      if (filterBy.value === 'purchased') {
        return (meta.wanted as number) <= (meta.purchased || 0)
      }
      if (filterBy.value === 'inStock') {
        return stockStatus.forBuyRequest(item, products.value) === ''
      }

      return true
    })
    .sort((a, b) => {
      const aMeta = a.metadata?.registry || {}
      const bMeta = b.metadata?.registry || {}

      if (sortBy.value === 'priority') {
        return priorities.indexOf(aMeta.priority as string) - priorities.indexOf(bMeta.priority as string)
      }
      if (sortBy.value === 'priceLow') {
        return computePriceSync(b, rogue.pinia!) - computePriceSync(a, rogue.pinia!)
      }
      if (sortBy.value === 'priceHigh') {
        return computePriceSync(a, rogue.pinia!) - computePriceSync(b, rogue.pinia!)
      }

      if (sortBy.value === 'remaining') {
        const aRemain = (aMeta.wanted as number) - (aMeta.purchased || 0)
        const bRemain = (bMeta.wanted as number) - (bMeta.purchased || 0)

        return bRemain - aRemain
      }

      return 0
    })
})

const visibleItems = computed(() => items.value.filter(item =>!productHasChanged(item, products.value)))

const name = computed(() => {
  // Get the registry recipient's name
  const recip = details.value.recipients?.[0] || {}

  return `${recip.firstName || ''} ${recip.lastName || ''}`
})

useHead(() => metadata({
  title  : `${rogue.__('Gear Registry')}${details.value?.name ? ` - ${details.value.name}` : ''}`,
  robots : 'noindex',
}, rogue) as ReactiveHead)

onMounted(() => {
  // Client side only. @todo should we SSR public registries?
  load()
})

async function load() {
  isLoading.value = true
  try {
    // Get the registry from the backend
    const urlKey = route.params.urlKey as string
    const url = `/svc_list/lists/registry/${urlKey}`

    // Password protected registries are requested with a POST call with body password
    const password = pass.value || SAVED_PASS[urlKey]

    let method: 'get' | 'post' = 'get'
    let body
    if (password) {
      method = 'post'
      body = { password }
    }

    registry.value = await rogue.fetch<PublicRegistryList>(url, method, body)

    registry.value.items.forEach(item => {
      item.metadata!.registry!.wanted = item.qty
      item.qty = 1
    })

    // Save the password, so using browser back/forward doesnt have to re-enter password
    if (pass.value) {
      SAVED_PASS[urlKey] = pass.value
    }
  } catch (e) {
    // Error might be "invalid_password" or "not_found"
    error.value = (e as { error?: string })?.error || null
  }
  isLoading.value = false
}
</script>
<style lang="scss" scoped>
.public-registry {
  margin: $space-xl auto;
  width: 100%;
}

.sort {
  display: flex;
  justify-content: space-between;
}

.from {
  text-transform: uppercase;
}

.err {
  margin-top: $space-s;
}

.item {
  padding: $space-xs 0;
  border-top: 1px solid $color-gray-light;
}

.enter-password {
  max-width: 400px;
  margin: $space-xxxl auto;
  .button {
    margin: 0 auto;
  }
}
</style>
